import { Grid } from '@material-ui/core';
import { CpfCnpjMask } from 'components/masks';
import { Button, MdSpace, SmSpace, TextField, NumberField, Section, Dropdown, AutoComplete } from 'm2m.components';
import ParticipanteAutoComplete from 'components/participante/participanteAutoComplete';
import React, { useState, useEffect, useCallback } from 'react';
import { ParticipanteResponse, FuncaoParticipante, RegistroStatus, BancoResponse } from 'common/genericTypes';
import { GETParticipantesResume } from 'requests/participante';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { TipoFundo, FundoInvestidorRequest } from 'common/genericTypes';

interface FundoInvestidorFormData {
  nomeFantasia: string;
  cnpjFundo: string;
  razaoSocialAdministrador: string;
  cnpjAdministrador: string;
  razaoSocialGestor: string;
  cnpjGestor: string;
  escrituradorId: string;
  nomeSimplificadoEscriturador: string;
  cnpjEscriturador: string;
  custodianteId: string;
  razaoSocialCustodiante: string;
  cnpjCustodiante: string;
  razaoSocialBancoLiquidante: string;
  cnpjBancoLiquidante: string;
  ispbBancoLiquidante: string;
  agencia: string;
  contaMovimento: string;
  codigoAtivo: string;
  classe: string;
  classificacaoANBIMA: string;
  codigoANBIMA: string;
  fundoExclusivo: boolean | string;
  tipoFundo: TipoFundo | string;
  fundoExclusivoUI?: { value: string; description: string };
  tipoFundoUI?: { value: string; description: string };
}

interface FundoInvestidorProps {
  cnpj: string;
  onSubmit: (data: FundoInvestidorFormData) => void;
  onCancel: () => void;
  investidorId?: string;
}

const mapStateToProps = (state: RootState) => ({
  investidorApi: state.investidorApi,
  participanteApi: state.participanteApi,
});

type Props = FundoInvestidorProps & ReturnType<typeof mapStateToProps>;

const FundoInvestidor: React.FC<Props> = (props) => {
  const { cnpj, onSubmit, onCancel, participanteApi, investidorId } = props;
  const dispatch = useDispatch();
  const getBancosApi = useSelector((state: RootState) => state.catalogoApi.getBancos);

  const [escrituradores, setEscrituradores] = useState<Array<ParticipanteResponse>>([]);
  const [custodiantes, setCustodiantes] = useState<Array<ParticipanteResponse>>([]);
  const [escriturador, setEscriturador] = useState<ParticipanteResponse | null>(null);
  const [custodiante, setCustodiante] = useState<ParticipanteResponse | null>(null);
  const [bancos, setBancos] = useState<Array<BancoResponse>>([]);
  const [banco, setBanco] = useState<BancoResponse | null>(null);

  const [formData, setFormData] = useState<FundoInvestidorFormData>({
    nomeFantasia: '',
    cnpjFundo: cnpj,
    razaoSocialAdministrador: '',
    cnpjAdministrador: '',
    razaoSocialGestor: '',
    cnpjGestor: '',
    escrituradorId: '',
    nomeSimplificadoEscriturador: '',
    cnpjEscriturador: '',
    custodianteId: '',
    razaoSocialCustodiante: '',
    cnpjCustodiante: '',
    razaoSocialBancoLiquidante: '',
    cnpjBancoLiquidante: '',
    ispbBancoLiquidante: '',
    agencia: '',
    contaMovimento: '',
    codigoAtivo: '',
    classe: '',
    classificacaoANBIMA: '',
    codigoANBIMA: '',
    fundoExclusivo: '',
    tipoFundo: '',
    fundoExclusivoUI: undefined,
    tipoFundoUI: undefined,
  });

  useEffect(() => {
    if (investidorId) {
      props.investidorApi
        .getFundoInvestidorByInvestidorId(dispatch, investidorId, true)
        .then(async (response) => {
          if (response && response.fundoInvestidor) {
            const fundoData = response.fundoInvestidor;

            const foundEscriturador = escrituradores.find((e) => e.id === fundoData.escrituradorId);
            if (foundEscriturador) {
              setEscriturador(foundEscriturador);
            }

            const foundCustodiante = custodiantes.find((c) => c.id === fundoData.custodianteId);
            if (foundCustodiante) {
              setCustodiante(foundCustodiante);
            }

            setFormData({
              ...fundoData,
              fundoExclusivo: fundoData.fundoExclusivo.toString(),

              fundoExclusivoUI: {
                value: fundoData.fundoExclusivo.toString(),
                description: fundoData.fundoExclusivo ? 'Sim' : 'Não',
              },
              tipoFundoUI: {
                value: fundoData.tipoFundo,
                description: fundoData.tipoFundo,
              },
            });
          }
        })
        .catch((error) => {
          console.error('Error loading fund investor data:', error);
        });
    }
  }, [investidorId, dispatch, escrituradores, custodiantes]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getBancos = useCallback(async () => {
    try {
      const result = await getBancosApi(dispatch, true);
      setBancos(result.banks);
      
      if (!banco && formData.razaoSocialBancoLiquidante) {
        const bancoAntigo = result.banks.find(
          (b) => b.shortname === formData.razaoSocialBancoLiquidante
        );
        setBanco(bancoAntigo ?? null);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  }, [dispatch, getBancosApi, banco, formData.razaoSocialBancoLiquidante]);

  const bancosFilter = (value: string) =>
    bancos.filter(
      (banco) =>
        banco.code.includes(value) ||
        banco.fullname.toLocaleLowerCase().includes(value) ||
        banco.ispb.includes(value) ||
        banco.shortname.toLocaleLowerCase().includes(value)
    );

  useEffect(() => {
    getBancos();
  }, [getBancos]);
  
  useEffect(() => {
    if (formData.razaoSocialBancoLiquidante && !banco && bancos.length > 0) {
      const foundBanco = bancos.find(b => b.shortname === formData.razaoSocialBancoLiquidante);
      if (foundBanco) {
        setBanco(foundBanco);
      }
    }
  }, [formData.razaoSocialBancoLiquidante, banco, bancos]);

  useEffect(() => {
    const fetchParticipantes = async () => {
      try {
        const result = await GETParticipantesResume(
          dispatch,
          {
            funcoes: [FuncaoParticipante.Escriturador, FuncaoParticipante.Custodiante],
            status: RegistroStatus.Aprovado,
            suspenso: false,
          },
          true
        );

        if (result) {
          const escrituradoresList = result.filter(
            (p) => p.funcoes && p.funcoes.some((f) => f.funcao === FuncaoParticipante.Escriturador)
          );

          const custodiantesList = result.filter(
            (p) => p.funcoes && p.funcoes.some((f) => f.funcao === FuncaoParticipante.Custodiante)
          );

          setEscrituradores(escrituradoresList);
          setCustodiantes(custodiantesList);
        }
      } catch (error) {
        console.error('Error fetching participantes:', error);
      }
    };

    fetchParticipantes();
  }, [dispatch]);

  const handleEscrituradorChange = useCallback(
    (value: ParticipanteResponse | null) => {
      setEscriturador(value);

      setFormData((prevData) => ({
        ...prevData,
        escrituradorId: value ? value.id.toString() : '',
        nomeSimplificadoEscriturador: value ? value.razaoSocial : '',
        cnpjEscriturador: value ? value.cnpj : '',
      }));

      const newErrors = { ...errors };
      ['escrituradorId', 'nomeSimplificadoEscriturador', 'cnpjEscriturador'].forEach((field) => {
        if (newErrors[field]) delete newErrors[field];
      });
      setErrors(newErrors);
    },
    [errors]
  );

  const handleCustodianteChange = useCallback(
    (value: ParticipanteResponse | null) => {
      setCustodiante(value);

      setFormData((prevData) => ({
        ...prevData,
        custodianteId: value ? value.id.toString() : '',
        razaoSocialCustodiante: value ? value.razaoSocial : '',
        cnpjCustodiante: value ? value.cnpj : '',
      }));

      const newErrors = { ...errors };
      ['custodianteId', 'razaoSocialCustodiante', 'cnpjCustodiante'].forEach((field) => {
        if (newErrors[field]) delete newErrors[field];
      });
      setErrors(newErrors);
    },
    [errors]
  );

  const handleBancoChange = useCallback(
    (value: BancoResponse | null) => {
      setBanco(value);

      setFormData((prevData) => ({
        ...prevData,
        razaoSocialBancoLiquidante: value ? value.shortname : '',
        cnpjBancoLiquidante: value ? value.cnpj : '',
        ispbBancoLiquidante: value ? value.ispb : '',
      }));

      const newErrors = { ...errors };
      ['razaoSocialBancoLiquidante', 'cnpjBancoLiquidante', 'ispbBancoLiquidante'].forEach((field) => {
        if (newErrors[field]) delete newErrors[field];
      });
      setErrors(newErrors);
    },
    [errors]
  );

  const handleChange = (field: keyof FundoInvestidorFormData, value: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));

    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const handleCnpjChange = (field: keyof FundoInvestidorFormData, value: string) => {
    value = value.replace(/[^a-zA-Z0-9]+/g, '').toUpperCase();
    if (value.length > 14) {
      return;
    }
    handleChange(field, value);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const requiredFields: Array<keyof FundoInvestidorFormData> = [
      'nomeFantasia',
      'cnpjFundo',
      'razaoSocialAdministrador',
      'cnpjAdministrador',
      'razaoSocialGestor',
      'cnpjGestor',
      'custodianteId',
      'razaoSocialCustodiante',
      'cnpjCustodiante',
      'razaoSocialBancoLiquidante',
      'codigoAtivo',
      'classificacaoANBIMA',
      'codigoANBIMA',
    ];

    if (!banco) {
      newErrors.razaoSocialBancoLiquidante = 'Campo obrigatório';
    }

    if (formData.fundoExclusivo === '') {
      newErrors.fundoExclusivo = 'Campo obrigatório';
    }

    if (formData.tipoFundo === '') {
      newErrors.tipoFundo = 'Campo obrigatório';
    }

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = 'Campo obrigatório';
      }
    });

    const cnpjFields: Array<keyof FundoInvestidorFormData> = [
      'cnpjFundo',
      'cnpjAdministrador',
      'cnpjGestor',
      'cnpjCustodiante',
    ];

    cnpjFields.forEach((field) => {
      const value = formData[field];
      if (value && value.toString().length !== 14) {
        newErrors[field] = 'CNPJ inválido';
      }
    });

    if (formData.cnpjEscriturador && formData.cnpjEscriturador.length !== 14) {
      newErrors.cnpjEscriturador = 'CNPJ inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      const { fundoExclusivoUI, tipoFundoUI, ...dataWithoutUIFields } = formData;

      const processedData = {
        ...dataWithoutUIFields,

        fundoExclusivo:
          typeof formData.fundoExclusivo === 'string'
            ? formData.fundoExclusivo === 'true'
            : Boolean(formData.fundoExclusivo),

        tipoFundo:
          typeof formData.tipoFundo === 'string' && (formData.tipoFundo === '' || !formData.tipoFundo)
            ? TipoFundo.FIF
            : formData.tipoFundo,

        escrituradorId: formData.escrituradorId || '',
      };

      if (investidorId) {
        try {
          const result = await props.investidorApi.putFundoInvestidor(
            dispatch,
            investidorId,
            processedData as FundoInvestidorRequest,
            true
          );
          onSubmit(processedData);
        } catch (error) {
          console.error('Error updating fund investor:', error);
          alert('Erro ao atualizar fundo investidor. Por favor, tente novamente.');
        }
      } else {
        onSubmit(processedData);
      }
    } else {
      console.error('Form validation failed:', errors);

      alert('Por favor, preencha todos os campos obrigatórios.');
    }
  };

  return (
    <Grid>
      <Section title="Informações do Fundo" defaultGrid>
        <TextField
          label="Nome Fantasia"
          value={formData.nomeFantasia}
          onChange={(value) => handleChange('nomeFantasia', value)}
          error={!!errors.nomeFantasia}
          errorMessage={errors.nomeFantasia}
          required
        />
        <TextField
          label="CNPJ do Fundo"
          value={CpfCnpjMask(formData.cnpjFundo)}
          onChange={(value) => handleCnpjChange('cnpjFundo', value)}
          error={!!errors.cnpjFundo}
          errorMessage={errors.cnpjFundo}
          required
          disabled
        />
        <TextField
          label="Código do Ativo"
          value={formData.codigoAtivo}
          onChange={(value) => handleChange('codigoAtivo', value)}
          error={!!errors.codigoAtivo}
          errorMessage={errors.codigoAtivo}
          required
        />
        <TextField
          label="Classe"
          value={formData.classe}
          onChange={(value) => handleChange('classe', value)}
          error={!!errors.classe}
          errorMessage={errors.classe}
        />
        <TextField
          label="Classificação ANBIMA"
          value={formData.classificacaoANBIMA}
          onChange={(value) => handleChange('classificacaoANBIMA', value)}
          error={!!errors.classificacaoANBIMA}
          errorMessage={errors.classificacaoANBIMA}
          required
        />
        <TextField
          label="Código ANBIMA"
          value={formData.codigoANBIMA}
          onChange={(value) => handleChange('codigoANBIMA', value)}
          error={!!errors.codigoANBIMA}
          errorMessage={errors.codigoANBIMA}
          required
        />
        <Dropdown
          label="Tipo do Fundo"
          value={formData.tipoFundoUI?.value || formData.tipoFundo || ''}
          onChange={(value) => {
            const options = [
              { value: '', description: 'Selecione' },
              { value: TipoFundo.ETF, description: 'ETF' },
              { value: TipoFundo.FIDC, description: 'FIDC' },
              { value: TipoFundo.FII, description: 'FII' },
              { value: TipoFundo.FIP, description: 'FIP' },
              { value: TipoFundo.FIF, description: 'FIF' },
              { value: TipoFundo.OFFSHORE, description: 'OFFSHORE' },
              { value: TipoFundo.INEXISTENTE, description: 'INEXISTENTE' },
              { value: TipoFundo.OUTROS, description: 'OUTROS' },
            ];
            const option = options.find((opt) => opt.value === value) || options[0];

            handleChange('tipoFundoUI', option);
            handleChange('tipoFundo', value);
          }}
          error={!!errors.tipoFundo}
          required
          options={[
            { value: '', description: 'Selecione' },
            { value: TipoFundo.ETF, description: 'ETF' },
            { value: TipoFundo.FIDC, description: 'FIDC' },
            { value: TipoFundo.FII, description: 'FII' },
            { value: TipoFundo.FIP, description: 'FIP' },
            { value: TipoFundo.FIF, description: 'FIF' },
            { value: TipoFundo.OFFSHORE, description: 'OFFSHORE' },
            { value: TipoFundo.INEXISTENTE, description: 'INEXISTENTE' },
            { value: TipoFundo.OUTROS, description: 'OUTROS' },
          ]}
        />
        <Dropdown
          label="Fundo Exclusivo"
          value={
            formData.fundoExclusivoUI?.value ||
            (formData.fundoExclusivo === true ? 'true' : formData.fundoExclusivo === false ? 'false' : '')
          }
          onChange={(value) => {
            const options = [
              { value: '', description: 'Selecione' },
              { value: 'true', description: 'Sim' },
              { value: 'false', description: 'Não' },
            ];
            const option = options.find((opt) => opt.value === value) || options[0];

            handleChange('fundoExclusivoUI', option);
            handleChange('fundoExclusivo', value === 'true');
          }}
          error={!!errors.fundoExclusivo}
          required
          options={[
            { value: '', description: 'Selecione' },
            { value: 'true', description: 'Sim' },
            { value: 'false', description: 'Não' },
          ]}
        />
      </Section>

      <Section title="Administrador" defaultGrid>
        <TextField
          label="Razão Social do Administrador"
          value={formData.razaoSocialAdministrador}
          onChange={(value) => handleChange('razaoSocialAdministrador', value)}
          error={!!errors.razaoSocialAdministrador}
          errorMessage={errors.razaoSocialAdministrador}
          required
        />
        <TextField
          label="CNPJ do Administrador"
          value={CpfCnpjMask(formData.cnpjAdministrador)}
          onChange={(value) => handleCnpjChange('cnpjAdministrador', value)}
          error={!!errors.cnpjAdministrador}
          errorMessage={errors.cnpjAdministrador}
          required
        />
      </Section>

      <Section title="Gestor" defaultGrid>
        <TextField
          label="Razão Social do Gestor"
          value={formData.razaoSocialGestor}
          onChange={(value) => handleChange('razaoSocialGestor', value)}
          error={!!errors.razaoSocialGestor}
          errorMessage={errors.razaoSocialGestor}
          required
        />
        <TextField
          label="CNPJ do Gestor"
          value={CpfCnpjMask(formData.cnpjGestor)}
          onChange={(value) => handleCnpjChange('cnpjGestor', value)}
          error={!!errors.cnpjGestor}
          errorMessage={errors.cnpjGestor}
          required
        />
      </Section>

      <Section title="Escriturador" defaultGrid>
        <ParticipanteAutoComplete
          label="Escriturador"
          participantes={escrituradores}
          participante={escriturador}
          onChange={handleEscrituradorChange}
          error={!!errors.escrituradorId}
          popupIndicatorTestId="dpnEscriturador"
        />
      </Section>

      <Section title="Custodiante" defaultGrid>
        <ParticipanteAutoComplete
          label="Custodiante"
          participantes={custodiantes}
          participante={custodiante}
          onChange={handleCustodianteChange}
          error={!!errors.custodianteId}
          popupIndicatorTestId="dpnCustodiante"
        />
      </Section>

      <Section title="Banco Liquidante" defaultGrid>
        <AutoComplete
          label="Banco Liquidante"
          options={bancos}
          getOptionLabel={(option: BancoResponse) => `${option.code} - ${option.shortname}`}
          value={banco}
          onChange={(newValue) => handleBancoChange(newValue)}
          filterOptions={(_options: Array<BancoResponse>, state) => bancosFilter(state.inputValue.toLocaleLowerCase())}
          required
        />
        <TextField
          label="Agência"
          value={formData.agencia}
          onChange={(value) => handleChange('agencia', value)}
          error={!!errors.agencia}
          errorMessage={errors.agencia}
        />
        <TextField
          label="Conta Movimento"
          value={formData.contaMovimento}
          onChange={(value) => handleChange('contaMovimento', value)}
          error={!!errors.contaMovimento}
          errorMessage={errors.contaMovimento}
        />
      </Section>

      <MdSpace />
      <Grid style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '40px' }}>
        <Button variant="outlined" onClick={onCancel}>
          CANCELAR
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          SALVAR
        </Button>
      </Grid>
      <MdSpace />
    </Grid>
  );
};

export default connect(mapStateToProps)(FundoInvestidor);
